import { ProgressBar } from "./ProgressBar";
import type { AffiliateStatusResponse } from "@codway-dev/shared";
import { useBilling } from "../../hooks/useBilling";
// useShopData + el CSS module solo alimentaban el bloque de notificaciones por
// correo (comentado abajo). Reactivar junto con ese bloque.
// import { useShopData } from "../../hooks/useShopData";
// import styles from "./PlanSection.module.css";

interface PlanSectionProps {
  affiliateStatus: AffiliateStatusResponse | null;
}

export const PlanSection = ({ affiliateStatus }: PlanSectionProps) => {
  const { subscription, loading } = useBilling();
  // const { shopEmail } = useShopData();

  // Every shop has a free subscription (ensured on install); fall back to free
  // defaults defensively if it hasn't loaded.
  const planName = subscription?.plan.name ?? "Gratis";
  const orderLimit = subscription?.plan.orderLimit ?? null;
  const usageCount = subscription?.usageCount ?? 0;
  const planPercent =
    orderLimit && orderLimit > 0
      ? Math.min(100, (usageCount / orderLimit) * 100)
      : 0;

  // Active affiliate bonus, shown as extra monthly capacity on top of the plan.
  const bonus =
    affiliateStatus && affiliateStatus.has_affiliate ? affiliateStatus : null;
  const bonusUsed = bonus
    ? Math.max(
        0,
        bonus.bonus_orders_per_period - bonus.current_period_remaining
      )
    : 0;
  const bonusPercent =
    bonus && bonus.bonus_orders_per_period > 0
      ? Math.min(100, (bonusUsed / bonus.bonus_orders_per_period) * 100)
      : 0;

  return (
    <s-stack direction="block" gap="large-200">
      <s-stack direction="inline" gap="small-200" alignItems="center">
        <s-icon type="chart-vertical" />
        <s-heading>Tu plan:</s-heading>
      </s-stack>
      <s-section>
        {loading ? (
          <s-spinner accessibility-label="Cargando tu plan…" />
        ) : (
          <s-stack direction="block" gap="base">
            <s-heading>{planName}</s-heading>
            <s-stack direction="block" gap="small-200">
              <s-text color="subdued">
                {orderLimit != null
                  ? `Tu plan activo en la aplicación es ${planName} con ${orderLimit} pedidos procesados cada mes. Este es tu progreso actual este mes:`
                  : `Tu plan activo en la aplicación es ${planName} con pedidos ilimitados.`}
              </s-text>
            </s-stack>

            {orderLimit != null && (
              <>
                <s-stack direction="inline" gap="base" alignItems="center">
                  <s-text type="strong">
                    {usageCount} / {orderLimit}
                  </s-text>
                </s-stack>
                <s-box>
                  <ProgressBar percent={planPercent} />
                </s-box>
              </>
            )}

            {bonus && (
              <>
                <s-divider />
                <s-stack direction="block" gap="small-200">
                  <s-stack
                    direction="inline"
                    gap="small-200"
                    alignItems="center"
                  >
                    <s-text type="strong">
                      Bonus de afiliado ({bonus.affiliate_name})
                    </s-text>
                    <s-badge tone="success">Activo</s-badge>
                  </s-stack>
                  <s-text color="subdued">
                    Pedidos bonus adicionales disponibles este mes:
                  </s-text>
                  <s-text type="strong">
                    {bonus.current_period_remaining} /{" "}
                    {bonus.bonus_orders_per_period}
                  </s-text>
                  <s-box>
                    <ProgressBar percent={bonusPercent} />
                  </s-box>
                </s-stack>
              </>
            )}

            <s-divider />
            <s-stack direction="block" gap="small-100">
              {/*
                NOTA: hoy el enforcement es "suave" (no se bloquean pedidos al
                superar el límite). El texto de bloqueo ("dejará de crear
                pedidos") queda PENDIENTE de confirmación del equipo para
                implementar el hard enforcement. Mientras tanto, copy veraz:
              */}
              <s-text>
                Los pedidos que superen tu límite mensual se cobran a tu bono de
                afiliado, si tienes uno activo.
              </s-text>
              <s-text>
                El límite de pedidos se restablecerá cada primer día del mes.
              </s-text>
              {/*
                Notificaciones por correo al 85%/100% del límite: todavía NO hay
                un sistema de envío de correos implementado, así que se ocultan
                hasta que exista. Reactivar este bloque (y el hook useShopData)
                cuando se construya esa funcionalidad.
                <span className={styles.notificationText}>
                  <s-text>
                    Recibirá 2 notificaciones automáticos cuando llegue a 85% y
                    100% de tu límite en
                  </s-text>
                  <span className={styles.inlineBadge}>
                    <s-badge>{shopEmail || "cargando..."}</s-badge>
                  </span>
                </span>
              */}
              {/* <s-button icon="chat" variant="secondary">
                Contáctanos
              </s-button> */}
            </s-stack>
          </s-stack>
        )}
      </s-section>
    </s-stack>
  );
};
